import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { format } from 'date-fns';
import {
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseCategory,
  PaymentMethod,
  Expense,
  ExpenseStatus,
  Receipt
} from '../../types';
import { StorageService } from '../../services/storage';
import { AdminService } from '../../services/admin';
import './ExpenseForm.css';

interface ExpenseFormProps {
  expense?: Expense;
  onSubmit: (data: CreateExpenseInput | UpdateExpenseInput, receipts?: File[]) => Promise<void>;
  onSaveDraft?: (data: CreateExpenseInput | UpdateExpenseInput) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
}

interface FormData {
  title: string;
  category: ExpenseCategory;
  amount: string;
  date: string;
  description: string;
  paymentMethod: PaymentMethod;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const ExpenseFormNew: React.FC<ExpenseFormProps> = ({
  expense,
  onSubmit,
  onSaveDraft,
  onCancel,
  isLoading = false,
  mode = 'create'
}) => {
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState<{ value: string, label: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch dynamic categories from DynamoDB
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await AdminService.getCategories();
        const dbCategories = categories
          .filter(cat => cat.isActive)
          .map(cat => ({
            value: cat.name,
            label: cat.name
          }));
        setDynamicCategories(dbCategories);
      } catch (error) {
        console.warn('Could not fetch dynamic categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
    watch
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      title: expense?.title || '',
      category: expense?.category || ExpenseCategory.OTHER,
      amount: expense?.amount?.toString() || '',
      date: expense?.date ? format(new Date(expense.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      description: expense?.description || '',
      paymentMethod: expense?.paymentMethod || PaymentMethod.PERSONAL_CARD
    }
  });

  useEffect(() => {
    if (expense) {
      reset({
        title: expense.title,
        category: expense.category,
        amount: expense.amount.toString(),
        date: format(new Date(expense.date), 'yyyy-MM-dd'),
        description: expense.description || '',
        paymentMethod: expense.paymentMethod
      });
    }
  }, [expense, reset]);

  const formatAmount = (value: string): string => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    return numericValue;
  };

  const handleFormSubmit = async (data: FormData) => {
    console.log('[ExpenseFormNew] ========== FORM SUBMIT HANDLER CALLED ==========');
    console.log('[ExpenseFormNew] Raw form data:', data);
    console.log('[ExpenseFormNew] Receipt files:', receiptFiles);
    console.log('[ExpenseFormNew] Form is valid:', isValid);
    console.log('[ExpenseFormNew] Is loading:', isLoading);

    // Validate receipt is attached (mandatory for new expenses)
    const hasExistingReceipts = expense?.receipts && expense.receipts.length > 0;
    if (receiptFiles.length === 0 && !hasExistingReceipts) {
      alert('Please attach at least one receipt before submitting the expense.');
      return;
    }

    const formattedData = {
      title: data.title.trim(),
      category: data.category,
      amount: parseFloat(data.amount),
      date: data.date,
      description: data.description.trim(),
      paymentMethod: data.paymentMethod
    };

    console.log('[ExpenseFormNew] Formatted data:', formattedData);
    console.log('[ExpenseFormNew] Calling onSubmit prop...');

    try {
      await onSubmit(formattedData, receiptFiles);
      console.log('[ExpenseFormNew] onSubmit completed successfully');
    } catch (error) {
      console.error('[ExpenseFormNew] onSubmit failed:', error);
      throw error;
    }
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;

    const data = watch();
    const formattedData = {
      title: data.title.trim(),
      category: data.category,
      amount: data.amount ? parseFloat(data.amount) : 0,
      date: data.date,
      description: data.description.trim(),
      paymentMethod: data.paymentMethod
    };

    setIsDraftSaving(true);
    try {
      await onSaveDraft(formattedData);
    } finally {
      setIsDraftSaving(false);
    }
  };

  // Receipt handling
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported. Please upload PDF, JPG, or PNG files only.';
    }
    if (file.size > maxFileSize) {
      return `File size exceeds 10MB limit.`;
    }
    return null;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert(`Upload errors:\n${errors.join('\n')}`);
    }

    setReceiptFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setReceiptFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string): string => {
    if (type.includes('pdf')) return 'PDF';
    if (type.includes('image')) return 'IMG';
    return 'FILE';
  };

  const canEdit = !expense || expense.status === ExpenseStatus.DRAFT;

  // Hardcoded default categories
  const defaultCategories = [
    { value: ExpenseCategory.TRAVEL, label: 'Travel' },
    { value: ExpenseCategory.MEALS, label: 'Meals' },
    { value: ExpenseCategory.ACCOMMODATION, label: 'Accommodation' },
    { value: ExpenseCategory.TRANSPORTATION, label: 'Transportation' },
    { value: ExpenseCategory.OFFICE_SUPPLIES, label: 'Office Supplies' },
    { value: ExpenseCategory.SOFTWARE, label: 'Software' },
    { value: ExpenseCategory.TRAINING, label: 'Training' },
    { value: ExpenseCategory.MARKETING, label: 'Marketing' },
    { value: ExpenseCategory.ENTERTAINMENT, label: 'Entertainment' },
    { value: ExpenseCategory.OTHER, label: 'Other' }
  ];

  // Merge hardcoded + dynamic categories (avoid duplicates)
  const categoryOptions = [
    ...defaultCategories,
    ...dynamicCategories.filter(dc =>
      !defaultCategories.some(def => def.value.toLowerCase() === dc.value.toLowerCase())
    )
  ];

  const paymentOptions = [
    { value: PaymentMethod.PERSONAL_CARD, label: 'Personal Card' },
    { value: PaymentMethod.COMPANY_CARD, label: 'Company Card' },
    { value: PaymentMethod.CASH, label: 'Cash' },
    { value: PaymentMethod.BANK_TRANSFER, label: 'Bank Transfer' }
  ];

  return (
    <div className="expense-form-container">
      <div className="expense-form-card">
        <div className="expense-form-header">
          <h2>{mode === 'create' ? 'Create New Expense' : 'Edit Expense'}</h2>
          <p>Fill in the details below to {mode === 'create' ? 'submit your expense for approval' : 'update your expense'}</p>
          {expense && (
            <span className="status-badge">{expense.status}</span>
          )}
        </div>

        <div className="expense-form-body">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Expense Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={!canEdit || isLoading}
                    {...register('title', {
                      required: 'Title is required',
                      minLength: { value: 3, message: 'Title must be at least 3 characters' },
                      maxLength: { value: 100, message: 'Title must be less than 100 characters' }
                    })}
                    className={`form-input ${errors.title ? 'error' : ''}`}
                    placeholder="e.g., Client dinner meeting, Office supplies"
                  />
                  <span className="form-error">{errors.title ? errors.title.message : '\u00A0'}</span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Category <span className="required">*</span>
                  </label>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field }) => (
                      <select
                        disabled={!canEdit || isLoading}
                        {...field}
                        className={`form-select ${errors.category ? 'error' : ''}`}
                      >
                        {categoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  <span className="form-error">{errors.category ? errors.category.message : '\u00A0'}</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Amount <span className="required">*</span>
                  </label>
                  <div className="amount-input-wrapper">
                    <span className="currency-symbol">₹</span>
                    <Controller
                      name="amount"
                      control={control}
                      rules={{
                        required: 'Amount is required',
                        validate: (value) => {
                          const num = parseFloat(value);
                          if (isNaN(num)) return 'Please enter a valid amount';
                          if (num <= 0) return 'Amount must be greater than 0';
                          if (num > 999999.99) return 'Amount must be less than ₹1,000,000';
                          return true;
                        }
                      }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <input
                          type="text"
                          disabled={!canEdit || isLoading}
                          {...field}
                          value={value}
                          onChange={(e) => onChange(formatAmount(e.target.value))}
                          className={`form-input ${errors.amount ? 'error' : ''}`}
                          placeholder="0.00"
                        />
                      )}
                    />
                  </div>
                  <span className="form-error">{errors.amount ? errors.amount.message : '\u00A0'}</span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Expense Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    disabled={!canEdit || isLoading}
                    {...register('date', {
                      required: 'Date is required',
                      validate: (value) => {
                        const selectedDate = new Date(value);
                        const today = new Date();
                        const oneYearAgo = new Date();
                        oneYearAgo.setFullYear(today.getFullYear() - 1);
                        if (selectedDate > today) return 'Date cannot be in the future';
                        if (selectedDate < oneYearAgo) return 'Date cannot be more than one year ago';
                        return true;
                      }
                    })}
                    className={`form-input ${errors.date ? 'error' : ''}`}
                  />
                  <span className="form-error">{errors.date ? errors.date.message : '\u00A0'}</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Payment Method <span className="required">*</span>
                  </label>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    rules={{ required: 'Payment method is required' }}
                    render={({ field }) => (
                      <select
                        disabled={!canEdit || isLoading}
                        {...field}
                        className={`form-select ${errors.paymentMethod ? 'error' : ''}`}
                      >
                        {paymentOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  <span className="form-error">{errors.paymentMethod ? errors.paymentMethod.message : '\u00A0'}</span>
                </div>
              </div>

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    disabled={!canEdit || isLoading}
                    {...register('description', {
                      maxLength: { value: 500, message: 'Description must be less than 500 characters' }
                    })}
                    className={`form-textarea ${errors.description ? 'error' : ''}`}
                    placeholder="Add any additional details about this expense..."
                  />
                  <span className="form-error">{errors.description ? errors.description.message : '\u00A0'}</span>
                </div>
              </div>
            </div>

            {/* Receipt Upload Section */}
            {canEdit && (
              <div className="receipt-section">
                <div className="receipt-section-header">
                  <svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <h3>Upload Receipts <span className="required">*</span></h3>
                </div>

                {receiptFiles.length === 0 && !expense?.receipts?.length && (
                  <div className="receipt-required-hint" style={{ color: '#dc2626', fontSize: '13px', marginBottom: '8px', fontWeight: '500' }}>
                    At least one receipt is required to submit an expense
                  </div>
                )}

                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    style={{ display: 'none' }}
                  />
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div className="text-sm font-medium text-gray-700">
                    {isDragOver ? 'Drop files here' : 'Drag & drop receipts or click to browse'}
                  </div>
                </div>

                {/* Selected Files */}
                {receiptFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="text-sm font-medium text-gray-700" style={{ marginBottom: '12px', fontSize: '13px', fontWeight: '600' }}>
                      Selected Files ({receiptFiles.length})
                    </div>
                    {receiptFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: 'white',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          marginBottom: '8px',
                          color: '#1f2937'
                        }}
                      >
                        <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                          <span style={{ flexShrink: 0, padding: '4px 8px', backgroundColor: '#f1f5f9', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                            {getFileIcon(file.type)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className="text-sm font-medium"
                              style={{
                                fontSize: '13px',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                color: '#1f2937'
                              }}
                              title={file.name}
                            >
                              {file.name}
                            </div>
                            <div className="text-xs text-gray-500" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                          style={{
                            color: '#c62828',
                            fontSize: '12px',
                            fontWeight: '600',
                            padding: '6px 12px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            flexShrink: 0,
                            marginLeft: '12px'
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Form Actions */}
            {canEdit && (
              <div className="form-actions">
                <button
                  type="submit"
                  disabled={!isValid || isLoading || isDraftSaving || (receiptFiles.length === 0 && !(expense?.receipts?.length))}
                  className="btn btn-primary"
                  onClick={() => {
                    console.log('[ExpenseFormNew] Submit button clicked!');
                    console.log('[ExpenseFormNew] Button state - isValid:', isValid, 'isLoading:', isLoading, 'isDraftSaving:', isDraftSaving, 'receipts:', receiptFiles.length);
                    console.log('[ExpenseFormNew] Form errors:', errors);
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>📤 Submit for Approval</>
                  )}
                </button>

                {onSaveDraft && (
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={isLoading || isDraftSaving}
                    className="btn btn-secondary"
                  >
                    {isDraftSaving ? 'Saving...' : '💾 Save as Draft'}
                  </button>
                )}

                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading || isDraftSaving}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}

            {!canEdit && (
              <div className="info-box warning">
                <p>🔒 This expense cannot be edited because it has been submitted for approval.</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseFormNew;
